import * as React from 'react';
import Select from 'react-select';
import type { OptionTypeBase, ValueType } from 'react-select';
import type { CSSObject } from '@emotion/serialize';

import Modal from '../../../../../ui/Model';
import Button from '../../../../../shared/components/core-ui/button/button.component';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { useEffect, useMemo, useState } from 'react';

export interface FurnishingRequestFormOption extends OptionTypeBase {
  label: string;
  value: string;
  packageId?: string;
}

export interface FurnishingRequestSourceOption<TSource extends string> extends OptionTypeBase {
  label: TSource;
  value: TSource;
}

export interface FurnishingRequestFormModalProps<TSource extends string> {
  config: {
    showModal: boolean;
    title: string;
    submitLabel: string;
    submittingLabel: string;
  };
  data: {
    unitTypeOptions: FurnishingRequestFormOption[];
    roomsOptions: FurnishingRequestFormOption[];
    areaOptions: FurnishingRequestFormOption[];
    packageOptions: FurnishingRequestFormOption[];
    modelOptions: FurnishingRequestFormOption[];
    addonsOptions: FurnishingRequestFormOption[];
    sourceOptions: FurnishingRequestSourceOption<TSource>[];
  };
  initialValues: {
    fullName: string;
    email: string;
    phoneNumber: string;
    source: TSource | '';
    typeOfUnit: string;
    unitTypeGroup: string;
    areaName: string;
    addons: string[] | string[][];
    packageId: string;
    modelId: string;
    price: string;
  } | null;
  eventHandlers: {
    closeHandler: (didSubmit?: boolean) => void;
    submitHandler: (payload: {
      fullName: string | undefined;
      email: string | undefined;
      phoneNumber: string | undefined;
      source: TSource | undefined;
      typeOfUnit: string | undefined;
      unitTypeGroup: string | undefined;
      areaName: string | undefined;
      addons: string[] | undefined;
      packageId: number | undefined;
      modelId: number | undefined;
      price: number | undefined;
    }) => Promise<void>;
  };
}

const emailRegex = '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$';

const toOption = (options: FurnishingRequestFormOption[], value: string) =>
  options.find((opt) => opt.value === value) || null;

const toMultiOptions = (options: FurnishingRequestFormOption[], values: string[]) => {
  const normalizedValues = new Set(values.map((v) => v.trim().toLowerCase()));

  return options.filter((opt) => {
    const byValue = opt.value?.toString().trim().toLowerCase();
    const byLabel = opt.label?.toString().trim().toLowerCase();
    return normalizedValues.has(byValue) || normalizedValues.has(byLabel);
  });
};

const normalizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  // Accept both `string[]` and nested `string[][]` shapes.
  const flattened = value.flat(2);
  return flattened.filter((v): v is string => typeof v === 'string');
};

export function FurnishingRequestFormModal<TSource extends string>({
  config,
  data,
  initialValues,
  eventHandlers
}: FurnishingRequestFormModalProps<TSource>) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);
  const [source, setSource] = useState<FurnishingRequestSourceOption<TSource> | null>(null);
  const [unitType, setUnitType] = useState<FurnishingRequestFormOption | null>(null);
  const [numberOfRooms, setNumberOfRooms] = useState<FurnishingRequestFormOption | null>(null);
  const [areaName, setAreaName] = useState<FurnishingRequestFormOption | null>(null);
  const [addons, setAddons] = useState<FurnishingRequestFormOption[]>([]);
  const [packageId, setPackageId] = useState<FurnishingRequestFormOption | null>(null);

  const filteredAddonsOptions = useMemo(() => {
    const selectedPackageId = packageId?.value;
    const packageScoped = selectedPackageId
      ? data.addonsOptions.filter((addon) => addon.packageId === selectedPackageId)
      : data.addonsOptions;

    const seenLabels = new Set<string>();
    return packageScoped.filter((addon) => {
      const labelKey = addon.label?.toString().trim().toLowerCase();
      if (!labelKey) return true;
      if (seenLabels.has(labelKey)) return false;
      seenLabels.add(labelKey);
      return true;
    });
  }, [data.addonsOptions, packageId?.value]);

  const [modelId, setModelId] = useState<FurnishingRequestFormOption | null>(null);
  const [price, setPrice] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!config.showModal) return;

    if (!initialValues) {
      setName(undefined);
      setEmail(undefined);
      setPhoneNumber(undefined);
      setSource(null);
      setUnitType(null);
      setNumberOfRooms(null);
      setAreaName(null);
      setAddons([]);
      setPackageId(null);
      setModelId(null);
      setPrice(undefined);
      return;
    }

    setName(initialValues.fullName || undefined);
    setEmail(initialValues.email || undefined);
    setPhoneNumber(initialValues.phoneNumber || undefined);
    setSource(data.sourceOptions.find((opt) => opt.value === initialValues.source) || null);

    setUnitType(toOption(data.unitTypeOptions, initialValues.typeOfUnit));
    setNumberOfRooms(toOption(data.roomsOptions, initialValues.unitTypeGroup));
    setAreaName(toOption(data.areaOptions, initialValues.areaName));

    const packageScopedAddons = data.addonsOptions.filter(
      (addon) => addon.packageId === initialValues.packageId
    );

    const seenAddonLabels = new Set<string>();
    const uniquePackageScopedAddons = packageScopedAddons.filter((addon) => {
      const labelKey = addon.label?.toString().trim().toLowerCase();
      if (!labelKey) return true;
      if (seenAddonLabels.has(labelKey)) return false;
      seenAddonLabels.add(labelKey);
      return true;
    });

    setAddons(
      toMultiOptions(uniquePackageScopedAddons, normalizeStringArray(initialValues.addons))
    );
    setPackageId(toOption(data.packageOptions, initialValues.packageId));
    setModelId(toOption(data.modelOptions, initialValues.modelId));

    setPrice(initialValues.price || undefined);
  }, [
    config.showModal,
    data.addonsOptions,
    data.areaOptions,
    data.modelOptions,
    data.packageOptions,
    data.roomsOptions,
    data.sourceOptions,
    data.unitTypeOptions,
    initialValues
  ]);

  const isPriceValid = useMemo(() => {
    const trimmed = price?.trim();
    return trimmed !== '' && !Number.isNaN(Number(trimmed));
  }, [price]);

  const canSubmit = useMemo(() => {
    return name && name?.trim() !== '' && !isSubmitting;
  }, [name, isSubmitting]);

  const selectMenuPortalProps = useMemo(
    () => ({
      menuPortalTarget: document.body,
      menuPosition: 'fixed' as const,
      styles: {
        menuPortal: (base: CSSObject, _props: unknown) =>
          ({
            ...base,
            zIndex: 9999
          } as CSSObject)
      }
    }),
    []
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await eventHandlers.submitHandler({
        fullName: name?.trim(),
        email: email?.trim(),
        phoneNumber: phoneNumber?.trim(),
        source: source?.value as TSource,
        typeOfUnit: unitType?.value,
        unitTypeGroup: numberOfRooms?.value,
        areaName: areaName?.value,
        addons: addons.length > 0 ? addons.map((addon) => addon.value) : undefined,
        packageId: packageId?.value ? Number(packageId.value) : undefined,
        modelId: modelId?.value ? Number(modelId.value) : undefined,
        price: price ? Number(price?.trim()) : undefined
      });
      eventHandlers.closeHandler(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={config.showModal}
      onClose={() => eventHandlers.closeHandler(false)}
      containerClassName="lg:h-auto lg:max-h-[85vh] lg:w-[900px]"
      contentClassName="overflow-visible"
    >
      <div className="p-8 overflow-visible" style={{ width: '100%' }}>
        <h2 className="m-0 mb-6 text-4xl font-gilroy-medium text-neutral-500 font-semibold">
          {config.title}
        </h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <InputText
              data={{
                value: name ?? '',
                placeholder: 'Name',
                errorMessage: 'Name is required'
              }}
              config={{ required: true }}
              eventHandlers={{
                onChangeHandler: (value) => setName(value)
              }}
            />
          </div>

          <div>
            <InputText
              data={{
                value: email ?? '',
                placeholder: 'Email',
                errorMessage: 'Valid email is required'
              }}
              config={{ type: InputTextType.email, validationRegex: emailRegex }}
              eventHandlers={{
                onChangeHandler: (value) => setEmail(value)
              }}
            />
          </div>

          <div>
            <InputText
              data={{
                value: phoneNumber ?? '',
                placeholder: 'Phone Number',
                errorMessage: 'Phone number is required'
              }}
              config={{}}
              eventHandlers={{
                onChangeHandler: (value) => setPhoneNumber(value)
              }}
            />
          </div>

          <div>
            <InputText
              data={{
                value: price ?? '',
                placeholder: 'Price',
                errorMessage: 'Price must be numeric'
              }}
              config={{ type: InputTextType.number }}
              eventHandlers={{
                onChangeHandler: (value) => setPrice(value)
              }}
            />
          </div>
          <div>
            <Select<FurnishingRequestFormOption, false>
              options={data.areaOptions}
              value={areaName}
              onChange={(opt: ValueType<FurnishingRequestFormOption, false>) => setAreaName(opt)}
              placeholder="Area"
              isClearable
              {...selectMenuPortalProps}
            />
          </div>
          <div>
            <Select<FurnishingRequestSourceOption<TSource>, false>
              options={data.sourceOptions}
              value={source}
              onChange={(opt: ValueType<FurnishingRequestSourceOption<TSource>, false>) =>
                setSource(opt)
              }
              placeholder="Source"
              isClearable
              {...selectMenuPortalProps}
            />
          </div>
          <div>
            <Select<FurnishingRequestFormOption, false>
              options={data.unitTypeOptions}
              value={unitType}
              onChange={(opt: ValueType<FurnishingRequestFormOption, false>) => setUnitType(opt)}
              placeholder="Unit Type"
              isClearable
              {...selectMenuPortalProps}
            />
          </div>

          <div>
            <Select<FurnishingRequestFormOption, false>
              options={data.packageOptions}
              value={packageId}
              onChange={(opt: ValueType<FurnishingRequestFormOption, false>) => {
                const nextPackage = opt as FurnishingRequestFormOption | null;
                setPackageId(nextPackage);

                const nextPackageId = nextPackage?.value;
                if (!nextPackageId) {
                  setAddons([]);
                  return;
                }

                const allowedIds = new Set(
                  data.addonsOptions
                    .filter((addon) => addon.packageId === nextPackageId)
                    .map((addon) => addon.value)
                );
                setAddons((prev) => prev.filter((addon) => allowedIds.has(addon.value)));
              }}
              placeholder="Package"
              isClearable
              {...selectMenuPortalProps}
            />
          </div>

          <div>
            <Select<FurnishingRequestFormOption, false>
              options={data.modelOptions}
              value={modelId}
              onChange={(opt: ValueType<FurnishingRequestFormOption, false>) => setModelId(opt)}
              placeholder="Model"
              isClearable
              {...selectMenuPortalProps}
            />
          </div>
          <div>
            <Select<FurnishingRequestFormOption, false>
              options={data.roomsOptions}
              value={numberOfRooms}
              onChange={(opt: ValueType<FurnishingRequestFormOption, false>) =>
                setNumberOfRooms(opt)
              }
              placeholder="Number Of Rooms"
              isClearable
              {...selectMenuPortalProps}
            />
          </div>

          <div>
            <Select<FurnishingRequestFormOption, true>
              options={filteredAddonsOptions}
              value={addons}
              onChange={(opt: ValueType<FurnishingRequestFormOption, true>) =>
                setAddons([...(opt as ReadonlyArray<FurnishingRequestFormOption>)])
              }
              placeholder={packageId?.value ? 'Addons' : 'Select package first'}
              isMulti
              isDisabled={!packageId?.value}
              {...selectMenuPortalProps}
            />
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <button
            type="button"
            onClick={() => eventHandlers.closeHandler(false)}
            className="px-7 py-3 rounded-lg font-semibold text-[1.05rem] border border-[rgba(120,120,120,0.4)] text-[#555] bg-transparent hover:bg-[#555] hover:text-white hover:border-[#555] transition"
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? config.submittingLabel : config.submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
